// src/App.tsx
import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix, getPathSlug, isReservedSegment, sanitizeSlug } from '@/utils/slug';
import { routeConfigs } from '@/routes/routeConfigs'; //라우트 설정 및 레이아웃 설정 파일 
import { Header } from '@/components/organisms/Header/Header';
import { Footer } from '@/components/organisms/Footer/Footer';
import { AppLayout }   from '@/components/Layouts/AppLayout/AppLayout';
import { Container }   from '@/components/Layouts/Container/Container';
import { AcademyHeader } from '@/components/organisms/Header/AcademyHeader';
import { academySubscriptionApi, publicAcademyApi } from '@/services/api';
import { SessionSlugProvider, useSessionSlug } from '@/hooks/useSessionSlug';


export default function App() {
  const isAcademyAuthed = () => !!localStorage.getItem('academy_token');
  const isStudentAuthed = () => !!localStorage.getItem('student_token');
  const isAdminAuthed = () => !!localStorage.getItem('admin_token');
  
  const AcademyGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const { pathname } = useLocation();
    if (!isAcademyAuthed()) {
      const segments = pathname.split('/').filter(Boolean);
      const first = segments[0] || '';
      const slugPrefix = first && !isReservedSegment(first) ? `/${first}` : '';
      const dest = slugPrefix ? `${slugPrefix}/academy/login` : '/academy/login';
      return <Navigate to={dest} replace />;
    }
    return children;
  };

  const StudentGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const { pathname } = useLocation();
    if (!isStudentAuthed()) {
      const segments = pathname.split('/').filter(Boolean);
      const first = segments[0] || '';
      const slugPrefix = first && !isReservedSegment(first) ? `/${first}` : '';
      const dest = slugPrefix ? `${slugPrefix}/login` : '/login';
      return <Navigate to={dest} replace />;
    }
    return children;
  };

  const AdminGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    if (!isAdminAuthed()) {
      return <Navigate to="/admin/login" replace />;
    }
    return children;
  };

  // 학원 전역 접근 가드: 개설비/무료체험/정기결제 상태에 따른 차단 처리
  const AcademyAccessGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const { pathname } = useLocation();
    const [loading, setLoading] = useState(true);
    const [access, setAccess] = useState<'allowed' | 'blocked'>('allowed');
    const [blockedReason, setBlockedReason] = useState<'setup_fee_required' | 'payment_required' | 'suspended' | undefined>(undefined);
    const [freeTrialEnd, setFreeTrialEnd] = useState<string | null>(null);
    const { academyCode, status: sessionStatus, setMeta } = useSessionSlug();

    useEffect(() => {
      if (sessionStatus === 'idle' || sessionStatus === 'loading') {
        return;
      }
      if (!academyCode) {
        setAccess('allowed');
        setBlockedReason(undefined);
        setFreeTrialEnd(null);
        setLoading(false);
        setMeta({
          freeTrialEnd: null,
          currentPlan: null,
          inFreeTrial: false,
          subscriptionAccess: 'allowed'
        });
        return;
      }
      const run = async () => {
        try {
          setLoading(true);
          const res: any = await academySubscriptionApi.status({ academyCode });
          const data = res?.data || res;
          const a = String(data?.access || 'allowed') as 'allowed' | 'blocked';
          const br = data?.blockedReason as any;
          const ftEnd = data?.freeTrialEnd || null;
          const plan = (data?.currentPlan || '').toString().toLowerCase();
          setAccess(a);
          setBlockedReason(br);
          setFreeTrialEnd(ftEnd);
          setMeta({
            freeTrialEnd: data?.inFreeTrial ? ftEnd : null,
            currentPlan: plan || null,
            inFreeTrial: !!data?.inFreeTrial,
            subscriptionAccess: a
          });
        } catch {
          // 상태 확인 실패 시 접근 허용(방해 최소화)
          setAccess('allowed');
          setBlockedReason(undefined);
          setFreeTrialEnd(null);
          setMeta({
            freeTrialEnd: null,
            currentPlan: null,
            inFreeTrial: false,
            subscriptionAccess: 'allowed'
          });
        } finally {
          setLoading(false);
        }
      };
      run();
    }, [academyCode, sessionStatus, setMeta]);

    if (loading) {
      return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          로딩 중...
        </div>
      );
    }

    const pathSlug = getPathSlug(pathname);
    const slugPrefix = pathSlug ? `/${pathSlug}` : '';
    const isPlanPath =
      pathname.startsWith('/academy/mypage/plan') ||
      (slugPrefix ? pathname.startsWith(`${slugPrefix}/academy/mypage/plan`) : false);

    // 결제 차단 상태여도 요금제 결제 화면은 접근 허용
    if (isPlanPath) {
      return children;
    }

    if (access === 'allowed') {
      return children;
    }

    // 차단 화면 렌더링
    const goPlan = () => {
      const targetPrefix = slugPrefix || getSlugPrefix(pathname);
      window.location.href = `${targetPrefix}/academy/mypage/plan`;
    };
    const message =
      blockedReason === 'setup_fee_required'
        ? '서비스 개설비용 납부가 필요합니다.\n자세한 내용은 알파영어에 문의 해보세요.\n문의: 010-3203-5985' // [QA Fix] 서비스 개설비 연락처 노출
        : blockedReason === 'suspended'
        ? '학원 사정으로 서비스가 일시 중지되었습니다.'
        : '서비스 이용료 납부 후 시스템 사용이 가능합니다.';

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 24 }}>
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            background: '#ffffff',
            border: '1px solid #E5E7EB',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
            padding: 24,
            textAlign: 'center'
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: '#EEF2FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6366F1',
              fontWeight: 800,
              fontSize: 18
            }}
          >
            !
          </div>
          <p style={{ whiteSpace: 'pre-line', fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            {message}
          </p>
          {blockedReason === 'payment_required' && (
            <button
              onClick={goPlan}
              style={{
                padding: '10px 16px',
                background: '#6366F1',
                color: '#fff',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              이용료 납부 바로가기
            </button>
          )}
        </div>
      </div>
    );
  };

  // 학생 인증이 필요 없는 페이지 목록
  const publicStudentPaths = ['/', '/login', '/sign_up', '/find_id', '/playground', '/report'];
  
  // 학생 페이지인지 확인 (academy, admin이 아닌 페이지)
  const isStudentPage = (path: string) => {
    return !path.startsWith('/admin') && !path.startsWith('/academy');
  };
  
  // 학생 페이지 중 인증이 필요한지 확인
  const requiresStudentAuth = (path: string) => {
    // /report로 시작하는 경로는 공개 페이지
    if (path.startsWith('/report/')) {
      return false;
    }
    // slug 포함 공개 성적표 경로: /{slug}/report/**
    if (/^\/[^/]+\/report\//.test(path)) {
      return false;
    }
    // slug 루트(/{slug})는 공개
    if (/^\/[^/]+$/.test(path)) {
      return false;
    }
    // slug 인증/가입/아이디찾기 페이지는 공개
    if (/^\/[^/]+\/(login|sign_up|find_id)(\/|$)/.test(path)) {
      return false;
    }
    return isStudentPage(path) && !publicStudentPaths.includes(path);
  };

  return (
    <BrowserRouter>
    <SessionSlugProvider>
    <SlugEnforcer />
    <Routes>
        {routeConfigs.map(({ path, element, background, containerProps, hideHeader, hideFooter }) => {
          // 학원/관리자 인덱스 페이지 처리 (리다이렉트)
          if (path === '/academy' || path === '/admin') {
            return (
              <Route
                key={path}
                path={path}
                element={element}
              />
            );
          }
          
          // 관리자 페이지는 독립적인 레이아웃 사용
          if (path.startsWith('/admin/')) {
            // 로그인 페이지는 가드 없이 접근 가능
            if (path === '/admin/login') {
              return (
                <Route
                  key={path}
                  path={path}
                  element={element}
                />
              );
            }
            // 그 외 관리자 페이지는 인증 가드 적용
            return (
              <Route
                key={path}
                path={path}
                element={
                  <AdminGuard>
                    {element}
                  </AdminGuard>
                }
              />
            );
          }
          
          // 학원 페이지: 슬러그 포함 경로까지 처리
          const isAcademyPath = path.startsWith('/academy/') || /^\/[^/]+\/academy\//.test(path);
  const isAcademyAuthFree = 
    path === '/academy/login' ||
    path === '/academy/sign_up' ||
    path.startsWith('/academy/find_') ||
    path.startsWith('/academy/paper/payment/success') ||
    path.startsWith('/academy/paper/payment/fail') ||
    /^\/[^/]+\/academy\/(login|sign_up|find_|paper\/payment\/(success|fail))/.test(path);
          if (isAcademyPath && !isAcademyAuthFree) {
            return (
              <Route
                key={path}
                path={path}
                element={
                  <AcademyGuard>
                    <AppLayout background={background}>
                      <Header />
                      <AcademyHeader />
                      <Container {...containerProps}>
                        <AcademyAccessGuard>
                          {element}
                        </AcademyAccessGuard>
                      </Container>
                      <Footer />
                    </AppLayout>
                  </AcademyGuard>
                }
              />
            );
          }
          if (isAcademyPath && isAcademyAuthFree) {
            return (
              <Route
                key={path}
                path={path}
                element={element}
              />
            );
          }
          
          // 학생 페이지: 인증이 필요한 페이지에만 StudentGuard 적용
          if (requiresStudentAuth(path)) {
            return (
              <Route
                key={path}
                path={path}
                element={
                  <StudentGuard>
                    <AppLayout background={background}>
                      <Header />
                      <AcademyHeader />
                      <Container {...containerProps}>{element}</Container>
                      <Footer />
                    </AppLayout>
                  </StudentGuard>
                }
              />
            );
          }
          
          // 공개 페이지 (학생 로그인 필요 없음)
          return (
            <Route
              key={path}
              path={path}
              element={
                <AppLayout background={background}>
                  {!hideHeader && <Header />}
                  {!hideHeader && <AcademyHeader />}
                  <Container {...containerProps}>{element}</Container>
                  {!hideFooter && <Footer />}
                </AppLayout>
              }
            />
          );
        })}
        {/* Catch-all: slug 유지하여 로그인으로 리다이렉트 */}
        <Route
          path="*"
          element={
            <CatchAllRedirect />
          }
        />
    </Routes>
    </SessionSlugProvider>
  </BrowserRouter>
  );
}

const SlugEnforcer: React.FC = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const slugValidationRef = useRef<{ slug: string; valid: boolean } | null>(null);
  const { slug: sessionSlug, refresh: refreshSessionSlug } = useSessionSlug();

  useEffect(() => {
    const segments = pathname.split('/').filter(Boolean);
    const first = segments[0] || '';
    if (!first || isReservedSegment(first)) {
      return;
    }
    const sanitized = sanitizeSlug(first);
    if (!sanitized) {
      redirectToBase(segments, navigate);
      return;
    }
    if (slugValidationRef.current && slugValidationRef.current.slug === sanitized && slugValidationRef.current.valid) {
      return;
    }
    let cancelled = false;
    const verifySlug = async () => {
      try {
        const result = await publicAcademyApi.getByUrl(sanitized);
        const valid = !!result && result.success !== false && !!(result.academyCode || result.name);
        if (cancelled) return;
        slugValidationRef.current = { slug: sanitized, valid };
        if (valid) {
          return;
        }
      } catch {
        if (cancelled) return;
        slugValidationRef.current = { slug: sanitized, valid: false };
      }
      redirectToBase(segments, navigate);
    };
    verifySlug();
    return () => {
      cancelled = true;
    };
  }, [pathname, navigate]);

  useEffect(() => {
    try {
      const isAcademyAuthed = !!localStorage.getItem('academy_token');
      const isStudentAuthed = !!localStorage.getItem('student_token');
      const pathSlug = getPathSlug(pathname);
      const first = pathname.split('/').filter(Boolean)[0] || '';
      const ensureSlug = (target: string, keepFirstSegment: boolean) => {
        const nextPath = buildSluggedPath(target, pathname, keepFirstSegment);
        if (nextPath && nextPath !== pathname) {
          navigate(`${nextPath}${search}`, { replace: true });
          return true;
        }
        return false;
      };

      if ((isAcademyAuthed || isStudentAuthed) && !sessionSlug) {
        refreshSessionSlug();
      }

      if (isAcademyAuthed && sessionSlug) {
        const segments = pathname.split('/').filter(Boolean);
        const isAdminPath = first === 'admin';
        const hasAcademySegment = segments.includes('academy');
        if (!isAdminPath && hasAcademySegment) {
          if (!pathSlug || pathSlug !== sessionSlug) {
            if (ensureSlug(sessionSlug, segments[0] === 'academy')) {
            return;
          }
          }
        }
      }

      if (isStudentAuthed && sessionSlug) {
        const isAdminPath = first === 'admin';
        const isAcademyPath = first === 'academy';
        if (!isAdminPath && !isAcademyPath) {
          if (!pathSlug || pathSlug !== sessionSlug) {
            if (ensureSlug(sessionSlug, false)) {
          return;
            }
          }
        }
      }

      // 학생 주요 경로에 슬러그가 없으면 보정 (세션 슬러그 기준)
      const studentRoots = new Set(['learning','homework','vocabulary','exam','mypage','playground']);
      const slugPrefix = sessionSlug ? `/${sessionSlug}` : getSlugPrefix(pathname);
      if (sessionSlug && studentRoots.has(first)) {
        if (!pathname.startsWith(`${slugPrefix}/`)) {
          navigate(`${slugPrefix}${pathname}${search}`, { replace: true });
          return;
        }
      }
    } catch {}
  }, [pathname, search, navigate, sessionSlug, refreshSessionSlug]);
  return null;
};

const redirectToBase = (segments: string[], navigate: ReturnType<typeof useNavigate>) => {
  const restSegments = segments.slice(1);
  const fallbackPath = restSegments.length ? `/${restSegments.join('/')}` : '/';
  navigate(fallbackPath || '/', { replace: true });
};
const CatchAllRedirect: React.FC = () => {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0] || '';
  const slugPrefix = first && !isReservedSegment(first) ? `/${first}` : '';
  const dest = slugPrefix ? `${slugPrefix}/login` : '/login';
  return <Navigate to={dest} replace />;
};

const buildSluggedPath = (slug: string, pathname: string, keepFirstSegment: boolean) => {
  const cleanSlug = sanitizeSlug(slug);
  if (!cleanSlug) return null;
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
  if (!segments.length) {
    return `/${cleanSlug}`;
  }
  if (segments[0] === cleanSlug) {
    return pathname;
  }
  const shouldKeepFirst = keepFirstSegment && (segments[0] === 'academy' || isReservedSegment(segments[0]));
  const rest = shouldKeepFirst ? segments : segments;
  const nextSegments = [cleanSlug, ...rest];
  return `/${nextSegments.join('/')}`;
};